import { Button, Card, Icon, Pill, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { PageHeader } from '@/app/layouts/PageHeader'
import checkout from '@/app/layouts/CheckoutLayout.module.css'

const FREE = [
  ['swap_horiz', '0.9% corridor spread'],
  ['redeem', '0.5% cashback on your card'],
  ['local_atm', '$200 at ATMs each month'],
  ['credit_card', 'Physical card costs $50'],
  ['schedule', 'Standard payout'],
] as const

const PLUS = [
  ['swap_horiz', '0.5% spread on the first $600'],
  ['redeem', '1% cashback, up to $600 a month'],
  ['local_atm', '$400 at ATMs each month'],
  ['credit_card', 'Free physical card and a 5 GB eSIM'],
  ['bolt', 'Same-day payout'],
] as const

const COMPARE = [
  ['Corridor spread', '0.9%', '0.5% first $600', 'up to $2.40 a month'],
  ['Card cashback', '0.5%', '1%', '$3.00 on $600 spent'],
  ['Free at ATMs', '$200', '$400', '$4.00 a month'],
  ['Physical card', '$50 once', 'Free', '$50 once'],
  ['Travel eSIM', '—', '5 GB a year', '$18 a year'],
  ['Payout speed', 'Standard', 'Same day', '—'],
] as const

const MONTHS = [
  ['July', '$3.10', '$7.99'],
  ['August', '$4.40', '$7.99'],
  ['September', '$4.30', '$7.99'],
] as const

export function PlansScreen() {
  return (
    <>
      <PageHeader
        title="Plans"
        subtitle="Free or Plus. We work out which one is worth it from what you actually sent."
      />

      <BentoGrid>
        <Card tone="light">
          <Card.Header>
            <Text variant="subtitle" weight="bold">Free</Text>
            <Pill tone="ink">Your plan</Pill>
          </Card.Header>
          <Stack direction="row" align="baseline" gap={2} className="pb-mt-3">
            <Text as="p" variant="title" weight="bold" numeric>$0</Text>
            <Text variant="caption" tone="secondary">a month, always</Text>
          </Stack>
          <Stack gap={3} className="pb-mt-5">
            {FREE.map(([icon, label]) => (
              <Stack key={label} direction="row" align="center" gap={3}>
                <Icon name={icon} size={16} />
                <Text variant="caption">{label}</Text>
              </Stack>
            ))}
          </Stack>
        </Card>

        <Card tone="dark">
          <Card.Header>
            <Text variant="subtitle" weight="bold" tone="onDark">Plus</Text>
            <Pill tone="accent">Recommended</Pill>
          </Card.Header>
          <Stack direction="row" align="baseline" gap={2} className="pb-mt-3">
            <Text as="p" variant="title" weight="bold" tone="accent" numeric>$7.99</Text>
            <Text variant="caption" tone="onDarkMuted">a month</Text>
          </Stack>
          <Stack gap={3} className="pb-mt-5">
            {PLUS.map(([icon, label]) => (
              <Stack key={label} direction="row" align="center" gap={3}>
                <Icon name={icon} size={16} />
                <Text variant="caption" tone="onDark">{label}</Text>
              </Stack>
            ))}
          </Stack>
          <Card.Footer>
            <Button size="lg" block iconEnd="arrow_forward">Start Plus</Button>
          </Card.Footer>
        </Card>

        <BentoSpan span={2}>
          <Card tone="light">
            <Card.Header>
              <Text variant="caption" weight="semibold">Is it worth it for you?</Text>
              <Pill tone="warning">Not yet</Pill>
            </Card.Header>
            <Text variant="caption" tone="secondary" className="pb-mt-2">
              Worked out from what you actually sent and spent, not from what you might.
            </Text>
            <Stack gap={0} className="pb-mt-4">
              {MONTHS.map(([month, saved, cost]) => (
                <div key={month} className={checkout.line}>
                  <Text variant="caption" weight="semibold" className="pb-grow">{month}</Text>
                  <Text variant="caption" weight="semibold" tone="positive" numeric>{saved}</Text>
                  <Text variant="caption" tone="secondary" numeric style={{ width: '4.5rem', textAlign: 'right' }}>{cost}</Text>
                </div>
              ))}
              <div className={checkout.ruleLight} />
              <div className={checkout.line}>
                <Text variant="eyebrow" weight="semibold" tone="secondary" className="pb-grow">Three months</Text>
                <Text variant="subtitle" weight="bold" tone="positive" numeric>$11.80</Text>
                <Text variant="subtitle" weight="bold" numeric style={{ width: '5.5rem', textAlign: 'right' }}>$23.97</Text>
              </div>
            </Stack>
            <Card.Footer>
              <Stack direction="row" align="center" gap={3}>
                <Icon name="schedule" size={18} />
                <Text variant="caption" tone="secondary">
                  Plus would have cost you more than it saved. We&rsquo;ll tell you the month it flips.
                </Text>
              </Stack>
            </Card.Footer>
          </Card>
        </BentoSpan>

        <BentoSpan span={4}>
          <Card tone="light">
            <Text variant="caption" weight="semibold">What changes, line by line</Text>
            <Text variant="eyebrow" tone="secondary" className="pb-mt-2">
              The last column is what the difference is actually worth in a month
            </Text>
            <Stack gap={0} className="pb-mt-4">
              {COMPARE.map(([label, free, plus, worth]) => (
                <div key={label} className={checkout.line}>
                  <Text variant="caption" weight="semibold" className="pb-grow">{label}</Text>
                  <Text variant="caption" tone="secondary" numeric style={{ width: '7rem', textAlign: 'right' }}>{free}</Text>
                  <Text variant="caption" weight="semibold" numeric style={{ width: '9rem', textAlign: 'right' }}>{plus}</Text>
                  <Text variant="caption" tone="secondary" className="pb-hide-sm" style={{ width: '10rem', textAlign: 'right' }}>{worth}</Text>
                </div>
              ))}
            </Stack>
            <Text variant="caption" tone="secondary" className="pb-mt-4">
              Both caps reset on the 1st. Above them, Plus behaves exactly like Free — nothing is
              charged extra.
            </Text>
          </Card>
        </BentoSpan>
      </BentoGrid>
    </>
  )
}
